import React, { useCallback, useEffect, useState } from "react";
import {
    FileWarning,
    Megaphone,
    ShieldOff,
} from "lucide-react";
import { toast } from "sonner";
import { whatsappMerkez } from "@/lib/api";
import { Button } from "@/components/ui/button";
import WhatsAppInboxPanel from "@/components/whatsapp/WhatsAppInboxPanel";
import WhatsAppInboxBotFlowEditor from "@/components/whatsapp/WhatsAppInboxBotFlowEditor";

export const EXTRA_TABS = [
    { id: "gelen", label: "Gelen kutusu" },
    { id: "bot-akis", label: "Bot akışı" },
    { id: "evrak", label: "Evrak avcısı" },
    { id: "kampanya", label: "Kampanya" },
    { id: "kvkk", label: "KVKK / Opt-out" },
];

export default function WhatsAppMerkezModuller({ tab, busyKey, runBusy }) {
    const [evrak, setEvrak] = useState([]);
    const [kampanyalar, setKampanyalar] = useState([]);
    const [optOut, setOptOut] = useState([]);
    const [kampanyaForm, setKampanyaForm] = useState({
        ad: "",
        sablon_kod: "evrak_hatirlatma",
        tip: "tahkim_mesaj_yok",
    });
    const [onizleme, setOnizleme] = useState([]);
    const [optTel, setOptTel] = useState("");

    const loadEvrak = useCallback(async () => {
        const data = await whatsappMerkez.evrakEksik({ limit: 200 });
        setEvrak(data.dosyalar || []);
    }, []);

    const loadKampanya = useCallback(async () => {
        const data = await whatsappMerkez.kampanyalar();
        setKampanyalar(data.kampanyalar || []);
    }, []);

    const loadOptOut = useCallback(async () => {
        const data = await whatsappMerkez.optOutList();
        setOptOut(data.opt_out || []);
    }, []);

    useEffect(() => {
        if (tab === "evrak") loadEvrak().catch(() => toast.error("Evrak listesi yüklenemedi."));
        if (tab === "kampanya") loadKampanya().catch(() => toast.error("Kampanyalar yüklenemedi."));
        if (tab === "kvkk") loadOptOut().catch(() => toast.error("Opt-out listesi yüklenemedi."));
    }, [tab, loadEvrak, loadKampanya, loadOptOut]);

    async function handleEvrakGonder(dosyaId) {
        await runBusy(`evrak-${dosyaId}`, async () => {
            const res = await whatsappMerkez.evrakGonder({ dosya_id: dosyaId });
            if (res.ok) toast.success("Evrak hatırlatması kuyruğa alındı.");
            else toast.error(res.detail || "Gönderilemedi.");
            await loadEvrak();
        });
    }

    async function handleKampanyaOnizle() {
        try {
            const data = await whatsappMerkez.kampanyaOnizle({ tip: kampanyaForm.tip });
            setOnizleme(data.hedefler || []);
            toast.success(`${(data.hedefler || []).length} hedef bulundu.`);
        } catch {
            toast.error("Önizleme başarısız.");
        }
    }

    async function handleKampanyaBaslat() {
        if (!kampanyaForm.ad.trim()) {
            toast.error("Kampanya adı girin.");
            return;
        }
        await runBusy("kampanya", async () => {
            const res = await whatsappMerkez.kampanyaOlustur({
                ad: kampanyaForm.ad,
                sablon_kod: kampanyaForm.sablon_kod,
                filtre: { tip: kampanyaForm.tip },
                hemen_calistir: true,
            });
            toast.success(`Kampanya: ${res.basarili || 0}/${res.toplam || 0} gönderildi.`);
            await loadKampanya();
        });
    }

    async function handleOptOutEkle() {
        if (!optTel.trim()) {
            toast.error("Telefon girin.");
            return;
        }
        try {
            await whatsappMerkez.optOutEkle({ telefon: optTel, sebep: "Manuel panel" });
            setOptTel("");
            toast.success("Opt-out kaydedildi.");
            loadOptOut();
        } catch {
            toast.error("Kayıt başarısız.");
        }
    }

    if (tab === "gelen") {
        return <WhatsAppInboxPanel busyKey={busyKey} runBusy={runBusy} />;
    }

    if (tab === "bot-akis") {
        return <WhatsAppInboxBotFlowEditor />;
    }

    if (tab === "evrak") {
        return (
            <section className="rounded-xl border border-amber-500/20 bg-[#0b1437]/80 p-4 space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2">
                        <FileWarning className="h-4 w-4 text-amber-400" />
                        Evrak eksik dosyalar ({evrak.length})
                    </h2>
                    <Button size="sm" variant="outline" onClick={loadEvrak} className="text-xs h-8">
                        Yenile
                    </Button>
                </div>
                <div className="overflow-x-auto rounded-lg border border-white/10">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase text-[#8b93ae] bg-black/20">
                                <th className="py-2 px-2">Dosya</th>
                                <th className="py-2 px-2">Müvekkil</th>
                                <th className="py-2 px-2">Eksik</th>
                                <th className="py-2 px-2 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {evrak.map((d) => (
                                <tr key={d.dosya_id} className="border-t border-white/5">
                                    <td className="py-2 px-2">{d.dosya_no}</td>
                                    <td className="py-2 px-2">{d.muvekkil_adi}</td>
                                    <td className="py-2 px-2 text-xs text-amber-200">
                                        {(d.eksik_evrak || []).join(", ") || "—"}
                                    </td>
                                    <td className="py-2 px-2 text-right">
                                        <Button
                                            size="sm"
                                            className="h-7 text-[10px] bg-[#25d366] text-white"
                                            disabled={d.gonderildi || !!busyKey}
                                            onClick={() => handleEvrakGonder(d.dosya_id)}
                                        >
                                            {d.gonderildi ? "Gönderildi" : "Hatırlat"}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        );
    }

    if (tab === "kampanya") {
        return (
            <section className="rounded-xl border border-violet-500/20 bg-[#0b1437]/80 p-4 space-y-4">
                <h2 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2">
                    <Megaphone className="h-4 w-4 text-violet-400" />
                    Segment kampanyası
                </h2>
                <div className="grid md:grid-cols-3 gap-3">
                    <input
                        placeholder="Kampanya adı"
                        value={kampanyaForm.ad}
                        onChange={(e) => setKampanyaForm((p) => ({ ...p, ad: e.target.value }))}
                        className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                    />
                    <select
                        value={kampanyaForm.tip}
                        onChange={(e) => setKampanyaForm((p) => ({ ...p, tip: e.target.value }))}
                        className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                    >
                        <option value="tahkim_mesaj_yok">Tahkim — mesaj gitmemiş</option>
                        <option value="yeni_muvekkil_30">Son 30 gün yeni müvekkil</option>
                        <option value="basvuru_aktif">Aktif başvuru dosyaları</option>
                    </select>
                    <select
                        value={kampanyaForm.sablon_kod}
                        onChange={(e) => setKampanyaForm((p) => ({ ...p, sablon_kod: e.target.value }))}
                        className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                    >
                        <option value="evrak_hatirlatma">evrak_hatirlatma</option>
                        <option value="tahkim_eksper">tahkim_eksper</option>
                        <option value="yeni_muvekkil">yeni_muvekkil</option>
                    </select>
                </div>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={handleKampanyaOnizle}>
                        Önizle
                    </Button>
                    <Button size="sm" className="bg-violet-600 text-white" onClick={handleKampanyaBaslat}>
                        Başlat (max 20/batch)
                    </Button>
                </div>
                {onizleme.length > 0 && (
                    <p className="text-xs text-[#8b93ae]">{onizleme.length} hedef önizlemede.</p>
                )}
                {kampanyalar.length > 0 && (
                    <div className="text-xs text-[#c5cad8] space-y-1">
                        {kampanyalar.slice(0, 5).map((k) => (
                            <div key={k.id} className="border border-white/10 rounded px-2 py-1">
                                {k.ad} — {k.basarili}/{k.toplam} · {k.durum}
                            </div>
                        ))}
                    </div>
                )}
            </section>
        );
    }

    if (tab === "kvkk") {
        return (
            <section className="rounded-xl border border-rose-500/20 bg-[#0b1437]/80 p-4 space-y-4">
                <h2 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2">
                    <ShieldOff className="h-4 w-4 text-rose-400" />
                    KVKK / Opt-out listesi
                </h2>
                <div className="flex gap-2">
                    <input
                        placeholder="905xxxxxxxxx"
                        value={optTel}
                        onChange={(e) => setOptTel(e.target.value)}
                        className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm flex-1"
                    />
                    <Button size="sm" onClick={handleOptOutEkle}>
                        Ekle
                    </Button>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                    {optOut.map((o) => (
                        <div
                            key={o.id}
                            className="flex justify-between items-center border border-white/10 rounded px-3 py-2 text-sm"
                        >
                            <div>
                                <span className="text-[#f5ecd6]">{o.telefon}</span>
                                {o.muvekkil_adi && (
                                    <span className="text-[#8b93ae] text-xs ml-2">{o.muvekkil_adi}</span>
                                )}
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-[10px]"
                                onClick={() =>
                                    whatsappMerkez.optOutSil(o.id).then(() => {
                                        toast.success("Silindi.");
                                        loadOptOut();
                                    })
                                }
                            >
                                Kaldır
                            </Button>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    return null;
}
